import { ScheduledSectionServant } from './scheduled_section_servant';
import { TermCourseServant } from '../../term_courses/servants/term_course_servant';
import { SectionStatusServant } from './section_status_servant';
import { ISection, ISectionType } from '../../../interfaces/scheduler_interfaces';
const _4 = require('lodash4');
import { TermScheduleFlags } from '../services/term_schedule_flags';

export class SectionRegistrationServant {
  static hasUnscheduledRequiredSiblingSections(section: ISection, allScheduledSections: ISection[]): boolean {
    if (!TermCourseServant.isMultiSectionCourse(section._course)) {
      return false;
    }

    // get required Section types for course of a selected section.(requiredSectionTypesForCourse)
    // filter those section types which are equal to seclected section session (requiredSectionTypesForSessionCourse)

    // get all scheduled sections for selected section course (scheduledSectionsForCourse).
    // get required scheduled section types of those secheduled sections.
    // filter those section types which are equal to seclected section session

    const requiredSectionTypesForSessionCourse = _4.filter(
      _4.filter(section._course._section_types, 'is_required'),
      (st) => st.session === section._section_type.session
    );

    const scheduledSectionsForCourse = _4.filter(
      allScheduledSections,
      (scheduledSection: ISection) =>
        _4.isEqual(scheduledSection.course, section.course) &&
        !SectionStatusServant.isStatusUnscheduled(scheduledSection)
    );

    const scheduledSectionsForCourseForThisSession = _4.filter(
      scheduledSectionsForCourse,
      (sec: ISection) => sec._section_type.session === section._section_type.session
    );

    const requiredSectionTypesOfScheduledSections = _4(scheduledSectionsForCourseForThisSession)
      .uniqBy('section_type')
      .map('_section_type')
      .filter('is_required')
      .value();
    return _4.size(requiredSectionTypesOfScheduledSections) < _4.size(requiredSectionTypesForSessionCourse);
  }

  static allSectionsAndSiblingsScheduled(sectionIds: number[], allSections: ISection[]) {
    const sectionsWithStatus: any[] = [];
    _4.map(sectionIds, (sectionId: number) => {
      const section = _4.find(allSections, { id: sectionId });
      sectionsWithStatus.push({
        linked_sections: sectionIds,
        sectionId,
        hasUnscheduledRequiredSiblings: section
          ? SectionRegistrationServant.hasUnscheduledRequiredSiblingSections(section, allSections)
          : false,
        section
      });
    });

    const allSectionsScheduledProperly = _4.every(sectionsWithStatus, ['hasUnscheduledRequiredSiblings', false]);
    return allSectionsScheduledProperly;
  }

  static getScheduledSiblingSections(section: ISection, allSections: ISection[], allSectionTypes: ISectionType[]) {
    return _4.filter(allSections, (sctn) => {
      const sctnSessionId = _4.toString(
        _4.get(allSectionTypes.find((section_type) => section_type.id === sctn.section_type)!, 'session')
      );
      const sectionSessionId = _4.toString(_4.get(section, '_section_type.session'));
      const hasSameSessionId = sctnSessionId === sectionSessionId;
      const isCourseSame = section['course'] === sctn.course;
      const isSectionScheduled =
        !SectionStatusServant.isStatusUnscheduled(sctn) && !SectionStatusServant.isStatusWithdrawn(sctn);
      return isCourseSame && isSectionScheduled && hasSameSessionId;
    });
  }

  static getSectionIdOfLinkedCrossCourses(
    sectionId: ISection['id'],
    allSections: ISection[],
    allSectionTypes: ISectionType[]
  ) {
    const _section = allSections.find((section) => section.id === sectionId);
    if (!_section) {
      return [];
    }
    let linkedSections = _section._linked_sections || [];
    if (_4.isEmpty(linkedSections)) {
      return [];
    }
    _4.each(linkedSections, (sectionVal, key) => {
      const linkedSection = _4.find(allSections, (section) => section.id === linkedSections[key]);
      if (_4.isEmpty(linkedSection)) {
        linkedSections = _4.uniq(linkedSections);
        return;
      }
      const linkedSectionIds = _4.get(linkedSection, '_linked_sections', []);

      const siblings = SectionRegistrationServant.getScheduledSiblingSections(
        linkedSection,
        allSections,
        allSectionTypes
      );
      const siblingSections = siblings ? _4.map(siblings, (item) => item['id']) : [];
      linkedSections = _4.uniq([...linkedSections, ...linkedSectionIds, ...siblingSections]);
    });
    _4.remove(linkedSections, (linkedSectionId) => linkedSectionId === sectionId);
    return linkedSections;
  }

  static hasUnscheduledCrossCourseCoreqs(
    section: ISection,
    scheduledSectionsWithMeta: ISection[] = [],
    allSectionTypes: ISectionType[]
  ) {
    const crossCourseLinkedSectionIds = SectionRegistrationServant.getSectionIdOfLinkedCrossCourses(
      section.id,
      scheduledSectionsWithMeta,
      allSectionTypes
    );

    const areAllCrossCourseLinkedSectionsScheduled = SectionRegistrationServant.allSectionsAndSiblingsScheduled(
      crossCourseLinkedSectionIds,
      scheduledSectionsWithMeta
    );
    return !_4.isEmpty(crossCourseLinkedSectionIds) && !areAllCrossCourseLinkedSectionsScheduled;
  }

  static isEligibleForRegistration = (
    section: ISection,
    scheduledSections: ISection[] = [],
    allSectionTypes: ISectionType[],
    termScheduleFlags: TermScheduleFlags
  ) => {
    if (
      SectionStatusServant.isStatusUnscheduled(section) ||
      SectionStatusServant.isStatusRegisteredOrCompleteOrInProgress(section)
    ) {
      return false;
    }

    const isCoReqLinkedSectionsSatisfied = _4.get(section, '_section_status.linked_section_coreqs_satisfied', false);
    if (!isCoReqLinkedSectionsSatisfied) {
      return false;
    }

    if (SectionRegistrationServant.hasUnscheduledCrossCourseCoreqs(section, scheduledSections, allSectionTypes)) {
      return false;
    }

    const SIS_AutoWaitlistToRegistrationEnabled = termScheduleFlags.isBatchRegisterWaitlistedStudentsEnabled();
    const preventSelectingWaitlistedSectionsForReg =
      SectionStatusServant.isStatusWaitlisted(section) && SIS_AutoWaitlistToRegistrationEnabled;
    if (preventSelectingWaitlistedSectionsForReg) {
      return false;
    }

    if (SectionRegistrationServant.hasUnscheduledRequiredSiblingSections(section, scheduledSections)) {
      return false;
    }

    if (
      SectionStatusServant.isStatusWaitlisted(section) &&
      termScheduleFlags.allowWaitlistedSectionsForRegistrationSelection()
    ) {
      return true;
    }

    if (!ScheduledSectionServant.hasAvailableSeats(section)) {
      return false;
    }

    return true;
  };
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/course_scheduler/servants/section_registration_servant.ts